import React, { type JSX } from 'react'
import styled from '@emotion/styled'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { toMoney } from '@mobi/utils/money'
import { trackBonusOfferClick } from './analytics'
import type { CampaignV2 } from './data/types'

type BonusBetSplittingTrayProps = {
  bonusBetToSplit: CampaignV2 | null
  isTrayOpen: boolean
  closeTray: () => void
  confirmSplitBonusBet: (data: SplitBonusBetInput) => void
  isBonusBetSplitPending: boolean
}

type SplitBonusBetInput = {
  bonusBetId: number
  numberOfTokens: number
}

type BetSplitOption = {
  id: number
  label: string
  isChecked: boolean
  numberOfTokens: number
}

export const BonusBetSplittingTray = ({
  bonusBetToSplit,
  isTrayOpen,
  closeTray,
  confirmSplitBonusBet,
  isBonusBetSplitPending: isBonusBetSplitPending,
}: BonusBetSplittingTrayProps): JSX.Element | null => {
  const [betSplitOptions, setBetSplitOptions] = React.useState<BetSplitOption[]>([])

  React.useEffect(() => {
    if (bonusBetToSplit) {
      let id = 0
      const formattedSplitOptions = bonusBetToSplit.splitTokenOptions.map(option => {
        return {
          id: id++,
          label: `${option.numberOfTokens} x ${toMoney(option.tokenAmount)}`,
          isChecked: false,
          numberOfTokens: option.numberOfTokens,
        }
      })
      setBetSplitOptions(formattedSplitOptions)
    }
  }, [bonusBetToSplit])

  const handleOptionClick = (id: number) => {
    setBetSplitOptions(prev =>
      prev.map(item =>
        item.id === id ? { ...item, isChecked: true } : { ...item, isChecked: false }
      )
    )
  }

  const handleConfirmSplitClick = () => {
    const activeSplitOption = betSplitOptions.find(item => item.isChecked === true)
    if (bonusBetToSplit && activeSplitOption) {
      confirmSplitBonusBet({
        bonusBetId: bonusBetToSplit.id,
        numberOfTokens: activeSplitOption.numberOfTokens,
      })
    }
  }

  const handleTermsClick = () => {
    if (bonusBetToSplit) {
      trackBonusOfferClick(bonusBetToSplit, 'Terms')
    }
  }

  const manyOptions = betSplitOptions.length > 4
  const isOptionChecked = betSplitOptions.some(option => option.isChecked)

  return (
    <Tray isOpen={isTrayOpen} headerTitle='Split Bonus Bet' onDismiss={closeTray}>
      <TrayContentStyled>
        <p>
          Choose how you’d like to split your bonus bet. All splits are final and cannot be undone.{' '}
          <a
            href='https://help.tabtouch.com.au/knowledge-base/bonus-offers-terms-conditions/'
            target='_blank'
            rel='noopener noreferrer'
            onClick={handleTermsClick}
          >
            T&Cs apply
          </a>
        </p>
        <ul className={manyOptions ? 'two-columns' : ''}>
          {betSplitOptions
            ? betSplitOptions.map(option => (
                <li
                  data-testid='split-option'
                  key={option.id}
                  onClick={() => handleOptionClick(option.id)}
                  className={option.isChecked ? 'is-checked' : ''}
                >
                  <span>{option.label}</span>
                  <RadioButton
                    id={String(option.id)}
                    type='Radio'
                    isChecked={option.isChecked}
                    size='sm'
                  />
                </li>
              ))
            : null}
        </ul>
        <Button
          isFullWidth={true}
          disabled={!isOptionChecked}
          onClick={() => handleConfirmSplitClick()}
          isLoading={isBonusBetSplitPending}
        >
          Confirm Split
        </Button>
      </TrayContentStyled>
    </Tray>
  )
}

const TrayContentStyled = styled.div({
  '> p': {
    fontSize: font.size.lg.fontSize,
    lineHeight: font.size.lg.lineHeight,
    fontFamily: font.family.primary,
    color: colors.neutral[700],
    textAlign: 'left',
    margin: `0 0 ${spacing.big}`,

    '> a': {
      color: colors.neutral[700],
      textUnderlineOffset: '3px',
    },
  },

  '> ul': {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 8,
    margin: `0 0 ${spacing.bigx1}`,
    padding: 0,

    '> li': {
      border: `1px solid ${colors.neutral[200]}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: spacing.sm,
      borderRadius: radius.lgx1,
      cursor: 'pointer',

      '> span': {
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.medium,
        lineHeight: font.size.lg.lineHeight,
        color: colors.neutral[900],
      },
    },
    '> li.is-checked': {
      backgroundColor: colors.lavender['50'],
      borderColor: colors.lavender['300'],
    },
  },
  '> ul.two-columns': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
})
